<template>
  <div class="report-generator">
    <div id="report">
      <section class="section">
        <div class="container">
          <!-- Header section -->
          <div class="columns mb-6">
            <div class="column is-half">
              <div class="content">
                <div class="is-size-5">Client Name: {{ details.clientName }}</div>
                <div class="is-size-5">Address: {{ details.address }}</div>
                <div class="is-size-5">Postcode: {{ details.postcode }}</div>
              </div>
            </div>
            <div class="column">
              <div class="has-text-right">
                <div class="is-size-5">{{ builderName | title }}</div>
                <div class="is-size-5">{{ contactEmail }}</div>
                <div class="is-size-5">{{ contactNumber }}</div>
              </div>
            </div>
          </div>

          <!-- Title -->
          <div class="has-text-centered">
            <h1 class="title is-2 has-text-black my-6">PRIMARY COST ESTIMATE</h1>
          </div>

          <!-- Content section -->
          <div class="content my-6">
            <p>
              The job type described in this estimate is {{jobType}}.
            </p>
            <p>
              This is a calculation of materials, plant
              and labour for estimating purposes only
              (not a fixed price model).
            </p>
            <p>
              The is estimate is based on the information provided
              and the responsibility is solely with you as the
              customer to ensure the correct information is inputted
            </p>
            <p>
              This is a guide only and is subject to
              change depending on the final design and
              specification.
            </p>
          </div>

          <!-- Build stages and costs -->
          <table class="table is-fullwidth my-6">
            <thead>
            <tr>
              <th class="has-text-white" style="width: 550px">Build Stage</th>
              <th class="has-text-white">Cost</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(buildStage, index) in buildStages" :key="index">
              <td style="width: 550px">
                <div class="build-stage-with-components">
                  <div class="is-size-5 has-text-weight-bold">{{ buildStage.name }}</div>
                  <div class="work-sections">
                    <span v-if="buildStage.workSectionsIncluded.length > 0" class="is-size-7 has-text-weight-bold">Includes: </span>
                    <span v-for="(workSection, index) in buildStage.workSectionsIncluded" :key="index"
                          class="is-size-7">{{ workSection }}<span
                        v-if="index < buildStage.workSectionsIncluded.length - 1">, </span></span>
                  </div>
                </div>
              </td>
              <td>{{ buildStage.value | currencyNotReduced }}</td>
            </tr>
            </tbody>
          </table>

          <div v-if="pcSums.length > 0" style="page-break-after: always;"></div>

          <!-- Build stages and costs -->
          <div v-if="pcSums.length > 0" class="has-text-black is-size-5 mb-3 mt-6">Extra Provisional Sums<span
              class="is-size-7">(not included in the build stage cost)</span></div>
          <table v-if="pcSums.length > 0" class="table is-fullwidth mb-6">
            <thead>
            <tr>
              <th class="has-text-white" style="width: 550px">Item Name</th>
              <th class="has-text-white">Cost</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(pcSum, index) in pcSums" :key="index">
              <td style="width: 550px">{{ pcSum.name }}</td>
              <td>{{ pcSum.value | currencyNotReduced }}</td>
            </tr>
            </tbody>
          </table>

          <div class="content my-6 w-100 is-pulled-right">
            <p class="is-size-5 has-text-weight-bold has-text-primary">
              Total: {{ totalWithoutVAT | currencyNotReduced }} (excluding VAT).
            </p>
          </div>
        </div>
      </section>
    </div>
    <b-button @click="generatePDF">Generate and download my report</b-button>
  </div>
</template>

<script>
import html2pdf from "html2pdf.js";
import filters from "../../../../../resource/module/filters";

export default {
  name: "report-generator",
  filters,
  props: {
    buildStages: {
      type: Array,
      required: true,
    },
    totalWithoutVAT: {
      type: Number,
      required: true
    },
    pcSums: {
      type: Array,
      required: true
    },
    jobType: {
      type: String,
      required: true
    }
  },
  data() {
    return {};
  },
  watch: {
    buildStages: {
      immediate: true,
      handler(newValue) {
        if (!Array.isArray(newValue) || newValue.length === 0) {
          this.showErrorToast('Invalid data format!');
          this.$router.back();
          return;
        }

        for (const stage of newValue) {
          if (typeof stage.name !== 'string' || typeof stage.value !== 'number') {
            this.showErrorToast('Invalid data in build stages!');
            this.$router.back();
            return;
          }
        }
      }
    }
  },
  computed: {
    builderName() {
      return this.$store.getters["user/loggedInUser"].name;
    },
    contactEmail() {
      return this.$store.getters["user/loggedInUser"].email;
    },
    contactNumber() {
      return this.$store.getters["user/loggedInUser"].telephone;
    },
    details() {
      const project = this.$store.getters["quote/selectedQuote"];
      let details = {};
      details.clientName = _.get(project, 'form.quoteInfo.customer.name', '');
      details.address = _.get(project, 'form.quoteInfo.customer.address.line1', '');
      details.postcode = _.get(project, 'form.quoteInfo.customer.address.postcode', '');

      return details;
    }
  },
  methods: {
    generatePDF() {
      const element = document.getElementById("report");
      const opt = {
        margin: 0.1,
        filename: "report.pdf",
        image: {type: "jpeg", quality: 0.98},
        html2canvas: {scale: 2},
        jsPDF: {unit: "in", format: "letter", orientation: "portrait"}
      };
      html2pdf().set(opt).from(element).save();
    },
    showErrorToast(message) {
      this.$buefy.toast.open({
        duration: 5000,
        message: message,
        type: 'is-danger',
        pauseOnHover: true,
        dismissible: true
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.report-generator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: $white;

  #report {
    width: 100%;
    max-width: 800px;
    margin-bottom: 20px;
    padding: 20px;
    border: 1px solid #ccc;
    border-radius: 5px;

    .build-stage-with-components {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
    }

    .table td:nth-child(1) {
      width: 70%; /* Adjust as needed */
      max-width: 70%;
    }

    .table th:nth-child(2),
    .table td:nth-child(2) {
      width: 350px;
      max-width: 350px;
    }

    .content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
    }
  }
}
</style>